import { type TranslationKey } from '@heidi/runtime/i18n';
import { LanguageCode } from '@heidi/schema/ml-scribe';

/**
 * Maps language codes to search aliases for improved searchability.
 * Users can search for these aliases to find the corresponding language.
 */
const languageSearchAliases: Partial<Record<LanguageCode, string[]>> = {
  zh: ['simplified chinese', 'mandarin', 'chinese'],
  [LanguageCode.zh_Hant]: ['traditional chinese', 'chinese'],
};

export type LanguageDisplayOptions = {
  /** When true, zh maps to Mandarin (spoken input). Otherwise Simplified (written output). */
  forInput?: boolean;
};

type TranslateLanguageName = (key: TranslationKey) => string;

/**
 * Maps language codes to their display translation keys.
 * Returns the full translation key (e.g., 'languageNames.zh-CN').
 * Input contexts use Mandarin; output contexts use Simplified Chinese.
 */
export function getLanguageDisplayKey(
  code: LanguageCode,
  options?: LanguageDisplayOptions
): TranslationKey {
  if (code === LanguageCode.zh) {
    return options?.forInput
      ? ('languageNames.zh-CN-input' as TranslationKey)
      : ('languageNames.zh-CN' as TranslationKey);
  }
  return `languageNames.${code}` as TranslationKey;
}

/** Returns the localized display name for a language code. */
export function getLocalizedLanguageName(
  code: LanguageCode,
  translate: TranslateLanguageName,
  options?: LanguageDisplayOptions
): string {
  return translate(getLanguageDisplayKey(code, options));
}

/**
 * Gets all searchable terms for a language code, including the code itself and any aliases.
 */
export function getLanguageSearchTerms(code: LanguageCode): string[] {
  const aliases = languageSearchAliases[code] || [];
  return [code.toLowerCase(), ...aliases];
}

/**
 * Checks if a language matches a search query, considering both the language name and search aliases.
 */
export function matchesLanguageSearch(
  languageCode: LanguageCode,
  languageName: string,
  searchQuery: string
): boolean {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return true;

  const nameMatch = languageName.toLowerCase().includes(query);
  if (nameMatch) return true;

  const searchTerms = getLanguageSearchTerms(languageCode);
  return searchTerms.some((term) => term.includes(query));
}
